"use client";

import { useAnimationSequence } from "@ui/hooks/useAnimationSequence";
import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { GridItem, IMAGE_MODELS, MODEL_NAMES, TIMING, TRANSITIONS } from "../types";
import IconZoomIn from "@ui/central-icons/icons/IconZoomIn";
import {
  PromptContentArea,
  ContentPlaceholder,
  ContentImage,
  ContentProcessingOverlay,
  PromptTextInput,
  TextPlaceholder,
  PromptControlBar,
  ControlItem,
  IconImage,
  SendButton,
  ModelSelector,
} from "../components";

const IMAGE_UPSCALE_STATES = [
  "IDLE",
  "OPEN_MODEL_SELECTOR",
  "SELECT_MODEL",
  "CLOSE_MODEL_SELECTOR",
  "UPLOAD_IMAGE",
  "SENDING",
  "PROCESSING",
  "RESULT",
  "FINISHED",
  "RESET",
] as const;

const UPSCALE_ORIGINAL_URL = "/assets/images/image-video-hero/upscale-pixelated.webp";
const UPSCALE_RESULT_URL = "/assets/images/image-video-hero/upscale-original.webp";

export function ImageUpscaleScene({
  onGenerationStarted,
  onComplete,
  autoStart = true,
}: {
  onGenerationStarted: (mediaUrl: string, mediaType: "image" | "video", label: string) => void;
  onComplete: (result: GridItem) => void;
  autoStart?: boolean;
}) {
  const t = useTranslations("ClientKeys.VideoImageGenerationHero");
  const { motion: m, start, store } = useAnimationSequence(IMAGE_UPSCALE_STATES);

  useEffect(() => {
    if (!autoStart) return;

    const timer = setTimeout(() => {
      start();
    }, 500);
    return () => clearTimeout(timer);
  }, [start, autoStart]);

  useEffect(() => {
    const unsubscribe = store.subscribe(
      state => state.state,
      currentState => {
        const currentStateName = IMAGE_UPSCALE_STATES[currentState];

        if (currentStateName === "PROCESSING") {
          onGenerationStarted(UPSCALE_ORIGINAL_URL, "image", t("processing_upscaling_image"));
        }

        if (currentStateName === "FINISHED") {
          onComplete({
            type: "imageUpscale",
            media: "",
            status: "complete",
            imageUrl: UPSCALE_RESULT_URL,
            isImageResult: true,
          });
        }
      },
    );
    return unsubscribe;
  }, [store, onGenerationStarted, onComplete, t]);

  return (
    <>
      <PromptContentArea sequencedMotion={m}>
        <ContentPlaceholder
          sequencedMotion={m}
          initial={{ opacity: 1 }}
          animate={{
            UPLOAD_IMAGE: { opacity: 0 },
            FINISHED: { opacity: 1 },
          }}
          transition={{
            UPLOAD_IMAGE: TRANSITIONS.medium,
            FINISHED: TRANSITIONS.instant,
          }}
        />

        <ContentImage
          sequencedMotion={m}
          src={UPSCALE_ORIGINAL_URL}
          alt='Image to upscale'
          render={["UPLOAD_IMAGE", "FINISHED"]}
          animatePresence={{ initial: false }}
          initial={{ opacity: 0 }}
          animate={{
            UPLOAD_IMAGE: { opacity: 1 },
            FINISHED: { opacity: 0 },
          }}
          transition={{
            UPLOAD_IMAGE: { ...TRANSITIONS.slow, delay: 0.2 },
            FINISHED: TRANSITIONS.instant,
          }}
        />

        <ContentProcessingOverlay
          sequencedMotion={m}
          render={["PROCESSING"]}
          initial={{ opacity: 0 }}
          animate={{
            PROCESSING: { opacity: 1 },
            RESULT: { opacity: 0 },
          }}
          transition={{
            PROCESSING: TRANSITIONS.medium,
            RESULT: TRANSITIONS.medium,
          }}
          spinner={
            <m.div
              animate={{
                PROCESSING: { rotate: 360 },
              }}
              transition={{
                PROCESSING: TRANSITIONS.spinner,
              }}
              className='tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full'
            />
          }
        />
      </PromptContentArea>

      <PromptTextInput
        sequencedMotion={m}
        style={{ overflow: "hidden" }}
        animate={{
          IDLE: { height: "auto", opacity: 1 },
          CLOSE_MODEL_SELECTOR: { height: 0, opacity: 0 },
        }}
        transition={{
          IDLE: TRANSITIONS.fast,
          CLOSE_MODEL_SELECTOR: { ...TRANSITIONS.medium, delay: TIMING.DELAY.SHORT },
        }}
      >
        <TextPlaceholder
          sequencedMotion={m}
          text={t("prompt_image_placeholder")}
          render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}
          animatePresence={{ initial: false }}
          initial={{ opacity: 1 }}
          animate={{
            IDLE: { opacity: 1 },
          }}
          exit={{ opacity: 0, transition: TRANSITIONS.medium }}
        />
      </PromptTextInput>

      <PromptControlBar
        sequencedMotion={m}
        controls={
          <>
            <ControlItem
              icon={<IconImage className='tw-size-5' />}
              sequencedMotion={m}
              label={
                <div className='tw-relative tw-h-full tw-flex tw-items-center'>
                  <m.span className='tw-type-sm' render={["IDLE", "-CLOSE_MODEL_SELECTOR"]}>
                    {MODEL_NAMES.NANO_BANANA}
                  </m.span>
                  <m.span className='tw-type-sm' render={["CLOSE_MODEL_SELECTOR"]}>
                    {MODEL_NAMES.IMAGE_UPSCALE}
                  </m.span>

                  <ModelSelector
                    sequencedMotion={m}
                    currentModel={MODEL_NAMES.NANO_BANANA}
                    targetModel={MODEL_NAMES.IMAGE_UPSCALE}
                    models={IMAGE_MODELS}
                    render={["OPEN_MODEL_SELECTOR", "SELECT_MODEL", "-CLOSE_MODEL_SELECTOR"]}
                    animatePresence={{ initial: false }}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{
                      OPEN_MODEL_SELECTOR: { opacity: 1, y: 0 },
                      SELECT_MODEL: { opacity: 1, y: 0 },
                    }}
                    exit={{ opacity: 0, y: -10, transition: TRANSITIONS.fast }}
                    transition={{
                      OPEN_MODEL_SELECTOR: { ...TRANSITIONS.medium, delay: TIMING.DELAY.SHORT },
                      SELECT_MODEL: TRANSITIONS.slow,
                    }}
                  />
                </div>
              }
            />
            <ControlItem
              icon={<IconZoomIn className='tw-size-5' />}
              label='2x'
              sequencedMotion={m}
              render={["CLOSE_MODEL_SELECTOR"]}
              className='tw-hidden md:tw-flex'
            />
          </>
        }
        button={
          <SendButton
            sequencedMotion={m}
            animate={{
              SENDING: {
                scale: [1, 0.92, 1],
                backgroundColor: ["#F5F3F1", "#dbdad9", "#F5F3F1"],
              },
            }}
            transition={{
              SENDING: TRANSITIONS.slow,
            }}
          />
        }
      />
    </>
  );
}
